import { createStreamClient } from './emptyApi';
import {
  api as graphqlApi,
  FetchMonitorOptionsQuery,
  SubscribeKanbanMonitorsDocument,
  SubscribeKanbanMonitorsSubscription,
  SubscribeKanbanMonitorsSubscriptionVariables,
  SubscribeStatusScreenMonitorDataDocument,
  SubscribeStatusScreenMonitorDataSubscription,
  SubscribeStatusScreenMonitorDataSubscriptionVariables,
  SubscribeStatusScreenStatusUpdatesDataDocument,
  SubscribeStatusScreenStatusUpdatesDataSubscription,
  SubscribeStatusScreenStatusUpdatesDataSubscriptionVariables
} from './generated/graphqlApi';

const enhancedApi = graphqlApi.enhanceEndpoints({
  endpoints: {
    FetchMonitorOptions: {
      transformResponse: (response: FetchMonitorOptionsQuery) => {
        const nodes = response.monitorOptions.nodes;
        return {
          ...response,
          monitorOptions: {
            ...response.monitorOptions,
            nodes: nodes.sort((a, b) => {
              if (!b.nickname || !a.nickname) {
                return !a.nickname && !b.nickname ? 0 : !a.nickname ? 1 : -1;
              }
              return a.nickname.localeCompare(b.nickname, undefined, {
                numeric: true
              });
            })
          }
        };
      }
    },

    FetchStatusScreenData: {
      async onCacheEntryAdded(
        { accountId },
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }
      ) {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const client = createStreamClient({
          getState,
          signal,
          options: { retryAttempts: Number.MAX_VALUE }
        });

        await cacheDataLoaded;

        try {
          const monitorEventSubscription =
            client.iterate<SubscribeStatusScreenMonitorDataSubscription>({
              query: SubscribeStatusScreenMonitorDataDocument.toString(),
              variables: {
                accountId
              } satisfies SubscribeStatusScreenMonitorDataSubscriptionVariables
            });

          void (async () => {
            try {
              for await (const result of monitorEventSubscription) {
                const { monitor } = result.data?.monitorEventsCreated ?? {};
                if (!monitor) {
                  return;
                }

                updateCachedData(draft => {
                  const draftMonitor = draft.account?.monitors.nodes.find(
                    m => m.id === monitor.id
                  );
                  if (draftMonitor) {
                    draftMonitor.operational = monitor.operational;
                    draftMonitor.operationStatus = monitor.operationStatus;
                    draftMonitor.charging = monitor.charging;
                  }
                });
              }
            } catch (err) {
              console.error('error updating cache data', err);
            } finally {
              abortController.abort();
            }
          })();

          const statusUpdatesSubscription =
            client.iterate<SubscribeStatusScreenStatusUpdatesDataSubscription>({
              query: SubscribeStatusScreenStatusUpdatesDataDocument.toString(),
              variables: {
                accountId
              } satisfies SubscribeStatusScreenStatusUpdatesDataSubscriptionVariables
            });

          void (async () => {
            try {
              for await (const result of statusUpdatesSubscription) {
                const update = result.data?.accountStatusUpdates;
                if (!update) {
                  continue;
                }
                switch (update.__typename) {
                  case 'AccountStatusUpdatesSubscriptionAccessoryPayload':
                    updateCachedData(draft => {
                      const accessories =
                        draft.account?.accessories.nodes ?? [];
                      for (const draftAccessory of accessories) {
                        if (draftAccessory.id === update.accessoryId) {
                          draftAccessory.operational = update.operational;
                          draftAccessory.operationStatus =
                            update.operationStatus;
                        } else if (
                          draftAccessory.parentAccessory?.id ===
                          update.accessoryId
                        ) {
                          draftAccessory.parentAccessory.operationStatus =
                            update.operationStatus;
                        }
                      }
                    });
                    break;
                  case 'AccountStatusUpdatesSubscriptionClimatePayload':
                    updateCachedData(draft => {
                      const draftAccessory =
                        draft.account?.accessories.nodes.find(
                          a => a.id === update.climateAccessoryId
                        );
                      if (
                        draftAccessory?.__typename === 'ClimateSensorAccessory'
                      ) {
                        if (draftAccessory.climate) {
                          draftAccessory.climate = {
                            ...draftAccessory.climate,
                            temperature: update.temperature
                          };
                        }
                      }
                    });
                    break;
                  case 'AccountStatusUpdatesSubscriptionClimateAlarmPayload':
                    updateCachedData(draft => {
                      for (const accessory of draft.account?.accessories.nodes.filter(
                        a => a.__typename === 'ClimateSensorAccessory'
                      ) ?? []) {
                        const alarm = accessory.climate?.alarms.find(
                          a => a.id === update.climateAlarmId
                        );
                        if (alarm) {
                          alarm.currentStatus = update.currentStatus;
                        }
                      }
                    });
                    break;
                }
              }
            } catch (err) {
              console.error('error updating cache data', err);
            } finally {
              abortController.abort();
            }
          })();

          await cacheEntryRemoved;
        } catch (err) {
          console.error('error handling stream subscription', err);
        } finally {
          abortController.abort();
        }
      }
    },

    FetchKanbanMonitors: {
      async onCacheEntryAdded(
        { accountId },
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }
      ) {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const client = createStreamClient({
          getState,
          signal,
          options: { retryAttempts: Number.MAX_VALUE }
        });

        await cacheDataLoaded;

        try {
          const subscription =
            client.iterate<SubscribeKanbanMonitorsSubscription>({
              query: SubscribeKanbanMonitorsDocument.toString(),
              variables: {
                accountId
              } satisfies SubscribeKanbanMonitorsSubscriptionVariables
            });

          void (async () => {
            try {
              for await (const result of subscription) {
                const event = result.data?.monitorEventsCreated;
                if (!event) {
                  return;
                }
                const { monitor } = event;
                if (!monitor) {
                  return;
                }

                updateCachedData(draft => {
                  const draftMonitor = draft.account?.monitors.nodes.find(
                    m => m.id === monitor.id
                  );
                  if (draftMonitor) {
                    draftMonitor.operational = monitor.operational;
                  }
                });
              }
            } catch (err) {
              console.error('error updating cache data', err);
            } finally {
              abortController.abort();
            }
          })();

          await cacheEntryRemoved;
        } catch (err) {
          console.error('error handling stream subscription', err);
        } finally {
          abortController.abort();
        }
      }
    }
  }
});

export * from './generated/graphqlApi';

export { enhancedApi as graphqlApi };
